import { meetsMinPlan } from './plans';
import type { PlanTier } from './auth';
// ── Accounting & VAT Types ─────────────────────────────────────────────────

/** UAE standard VAT rate — fixed by law, not configurable */
export const UAE_VAT_RATE = 5;

/** Tax mode: inclusive = menu prices include VAT, exclusive = VAT added on top */
export type TaxMode = 'inclusive' | 'exclusive';

/** Filing period for VAT returns */
export type FilingPeriod = 'monthly' | 'quarterly';

/** Invoice type */
export type InvoiceType = 'tax_invoice' | 'credit_note';

/** Audit log event types */
export type AccountingAuditEventType =
  | 'invoice_created'
  | 'invoice_voided'
  | 'credit_note_issued'
  | 'payment_received'
  | 'payment_refunded'
  | 'payment_voided'
  | 'tax_config_changed'
  | 'tax_mode_changed'
  | 'trn_updated'
  | 'order_voided';

// ── Tax Configuration ──────────────────────────────────────────────────────

export interface TaxConfig {
  id: string;
  organizationId: string;
  trn: string | null;
  isVatRegistered: boolean;
  vatRegistrationDate: string | null;
  taxMode: TaxMode;
  defaultTaxRate: number;
  invoicePrefix: string;
  creditNotePrefix: string;
  filingPeriod: FilingPeriod;
  // Accounting integration fields
  accountingSoftware: AccountingSoftware;
  accountingAccessToken: string | null;
  accountingRefreshToken: string | null;
  accountingTokenExpiresAt: string | null;
  accountingOrgId: string | null;
  accountingSyncEnabled: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: SyncStatus;
  lastSyncError: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Tax Invoice ────────────────────────────────────────────────────────────

export interface TaxInvoiceLineItem {
  name: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  taxAmount: number;
  total: number;
}

export interface TaxInvoice {
  id: string;
  organizationId: string;
  restaurantId: string;
  orderId: string | null;
  bookingId: string | null;
  invoiceType: InvoiceType;
  invoiceNumber: string;
  supplierName: string;
  supplierTrn: string | null;
  supplierAddress: string | null;
  buyerName: string | null;
  buyerTrn: string | null;
  buyerAddress: string | null;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  discountAmount: number;
  tipAmount: number;
  serviceFee: number;
  serviceFeeTax: number;
  paymentMethod: string | null;
  paymentStatus: string | null;
  originalInvoiceId: string | null;
  lineItems: TaxInvoiceLineItem[];
  currency: string;
  issuedAt: string;
  isVoided: boolean;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  createdAt: string;
}

// ── Audit Log ──────────────────────────────────────────────────────────────

export interface AccountingAuditEntry {
  id: string;
  organizationId: string;
  restaurantId: string | null;
  eventType: AccountingAuditEventType;
  entityType: string | null;
  entityId: string | null;
  description: string;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  performedBy: string;
  performedByName: string | null;
  performedByRole: string | null;
  createdAt: string;
}

// ── VAT Calculator (Pure Functions) ────────────────────────────────────────

export interface VatBreakdown {
  /** Amount before tax */
  netAmount: number;
  /** Tax amount */
  taxAmount: number;
  /** Total amount (net + tax) */
  totalAmount: number;
  /** Tax rate applied (e.g. 5) */
  taxRate: number;
  /** Tax mode used */
  taxMode: TaxMode;
}

/**
 * Calculate VAT breakdown for a given amount.
 *
 * Tax-inclusive: amount already includes VAT → extract it
 *   VAT = amount × rate / (100 + rate)
 *   Net = amount - VAT
 *
 * Tax-exclusive: amount is before VAT → add it
 *   VAT = amount × rate / 100
 *   Total = amount + VAT
 */
export function calculateVAT(
  amount: number,
  mode: TaxMode,
  rate: number = UAE_VAT_RATE,
): VatBreakdown {
  if (rate <= 0 || amount <= 0) {
    return {
      netAmount: round2(amount),
      taxAmount: 0,
      totalAmount: round2(amount),
      taxRate: rate,
      taxMode: mode,
    };
  }

  if (mode === 'inclusive') {
    // Amount includes VAT — extract it
    const taxAmount = round2((amount * rate) / (100 + rate));
    const netAmount = round2(amount - taxAmount);
    return {
      netAmount,
      taxAmount,
      totalAmount: round2(amount),
      taxRate: rate,
      taxMode: mode,
    };
  }

  // Exclusive — add VAT on top
  const taxAmount = round2((amount * rate) / 100);
  return {
    netAmount: round2(amount),
    taxAmount,
    totalAmount: round2(amount + taxAmount),
    taxRate: rate,
    taxMode: mode,
  };
}

/**
 * Calculate VAT for a split payment amount.
 * The last payment in a split absorbs rounding differences.
 */
export function calculateSplitVAT(
  splitAmount: number,
  mode: TaxMode,
  rate: number = UAE_VAT_RATE,
  isLastPayment: boolean = false,
  originalTotalVat?: number,
  previousSplitVatSum?: number,
): VatBreakdown {
  if (!isLastPayment || originalTotalVat === undefined || previousSplitVatSum === undefined) {
    return calculateVAT(splitAmount, mode, rate);
  }

  // Last payment absorbs rounding
  const remainingVat = round2(originalTotalVat - previousSplitVatSum);
  const netAmount = round2(splitAmount - remainingVat);

  return {
    netAmount,
    taxAmount: remainingVat,
    totalAmount: round2(splitAmount),
    taxRate: rate,
    taxMode: mode,
  };
}

/**
 * Calculate VAT on service fee (service fee is also taxable in UAE).
 */
export function calculateServiceFeeVAT(
  serviceFee: number,
  mode: TaxMode,
  rate: number = UAE_VAT_RATE,
): VatBreakdown {
  return calculateVAT(serviceFee, mode, rate);
}

/** Round to 2 decimal places (banker's rounding avoided — standard rounding) */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

// ── Order Tax Fields (extends Order interface) ─────────────────────────────

export interface OrderTaxFields {
  taxMode: TaxMode | null;
  taxRate: number;
  taxAmount: number;
  netAmount: number;
  invoiceNumber: string | null;
  creditNoteNumber: string | null;
  refundAmount: number;
  refundVatAmount: number;
  refundReason: string | null;
  refundedAt: string | null;
  refundedBy: string | null;
  isVoided: boolean;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
}

// ── VAT Summary (for reports) ──────────────────────────────────────────────

export interface VatSummary {
  periodStart: string;
  periodEnd: string;
  totalSales: number;
  totalVatCollected: number;
  netSales: number;
  totalRefunds: number;
  refundVat: number;
  totalVoids: number;
  netTaxable: number;
  netVatDue: number;
  orderCount: number;
  refundCount: number;
  voidCount: number;
  invoiceCount: number;
  creditNoteCount: number;
}

// ── Z-Report (Daily Closing) ───────────────────────────────────────────────

export interface ZReport {
  id: string;
  restaurantId: string;
  reportNumber: string;
  businessDate: string;
  periodStart: string;
  periodEnd: string;
  closedBy: string;
  closedByName: string | null;
  totalOrders: number;
  grossSales: number;
  discounts: number;
  netSales: number;
  vatCollected: number;
  cashSales: number;
  cashOrderCount: number;
  cardSales: number;
  cardOrderCount: number;
  onlineSales: number;
  onlineOrderCount: number;
  refundAmount: number;
  refundCount: number;
  voidAmount: number;
  voidCount: number;
  tipsCollected: number;
  openingBalance: number;
  cashIn: number;
  cashOut: number;
  expectedCash: number;
  actualCash: number | null;
  cashVariance: number | null;
  serviceFeeCollected: number;
  serviceFeeVat: number;
  deliveryOrders: number;
  deliveryRevenue: number;
  deliveryCommission: number;
  createdAt: string;
}

// ── Cash Drawer ────────────────────────────────────────────────────────────

export type CashEventType = 'opening' | 'cash_in' | 'cash_out' | 'count' | 'closing';

export interface CashDrawerEvent {
  id: string;
  restaurantId: string;
  eventType: CashEventType;
  amount: number;
  balanceAfter: number;
  reason: string | null;
  performedBy: string;
  performedByName: string | null;
  businessDate: string;
  createdAt: string;
}

// ── VAT Period Locks ───────────────────────────────────────────────────────

export interface VatPeriodLock {
  id: string;
  organizationId: string;
  periodStart: string;
  periodEnd: string;
  isLocked: boolean;
  lockedAt: string | null;
  lockedBy: string | null;
  lockedByName: string | null;
  filedReference: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Daily Sales Report ─────────────────────────────────────────────────────

export interface DailySalesRow {
  date: string;
  totalOrders: number;
  grossSales: number;
  vatCollected: number;
  netSales: number;
  cashSales: number;
  cardSales: number;
  onlineSales: number;
  refunds: number;
  tips: number;
}

export interface PaymentMethodBreakdown {
  method: string;
  orderCount: number;
  totalAmount: number;
  vatAmount: number;
  percentage: number;
}

// ── Consolidated VAT (Multi-branch) ────────────────────────────────────────

export interface BranchVatSummary {
  venueId: string;
  venueName: string;
  totalSales: number;
  vatCollected: number;
  invoiceCount: number;
  refundAmount: number;
  refundVat: number;
}

export interface ConsolidatedVatReport {
  organizationId: string;
  trn: string | null;
  periodStart: string;
  periodEnd: string;
  branches: BranchVatSummary[];
  totalSales: number;
  totalVat: number;
  totalRefunds: number;
  totalRefundVat: number;
  netTaxable: number;
  netVatDue: number;
  totalInvoices: number;
}

// ── Accounting Software Integration ────────────────────────────────────────

export type AccountingSoftware = 'zoho' | 'xero' | 'quickbooks' | 'none';
export type SyncStatus = 'idle' | 'syncing' | 'success' | 'failed';
export type SyncAction = 'create_invoice' | 'create_credit_note' | 'create_payment' | 'sync_contact' | 'update_invoice' | 'void_invoice';

export interface AccountingIntegrationStatus {
  provider: AccountingSoftware;
  isConnected: boolean;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: SyncStatus;
  lastSyncError: string | null;
  externalOrgId: string | null;
}

export interface AccountingSyncLogEntry {
  id: string;
  organizationId: string;
  invoiceId: string | null;
  provider: AccountingSoftware;
  action: SyncAction;
  status: 'pending' | 'success' | 'failed' | 'skipped';
  externalId: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export const ACCOUNTING_PROVIDERS: { id: AccountingSoftware; name: string; description: string }[] = [
  { id: 'zoho', name: 'Zoho Books', description: 'Popular UAE accounting software with native Arabic support' },
  { id: 'xero', name: 'Xero', description: 'Cloud accounting with strong multi-currency support' },
  { id: 'quickbooks', name: 'QuickBooks Online', description: 'Widely used small business accounting' },
];

// ── Timezone-Aware Period Boundaries ───────────────────────────────────────

/** Dubai timezone offset in ms (GMT+4, no DST) */
const DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000;

/** Get the current business date in Dubai timezone (YYYY-MM-DD) */
export function getDubaiBusinessDate(date: Date = new Date()): string {
  return new Date(date.getTime() + DUBAI_OFFSET_MS).toISOString().slice(0, 10);
}

/** Get UTC start/end timestamps for a Dubai business date range */
export function dubaiDateRangeToUtc(from: string, to: string): { startUtc: string; endUtc: string } {
  return {
    startUtc: `${from}T00:00:00+04:00`,
    endUtc: `${to}T23:59:59+04:00`,
  };
}

/** Get quarterly period boundaries for a Dubai date */
export function getQuarterBoundaries(date: string): { periodStart: string; periodEnd: string } {
  const d = new Date(date);
  const quarter = Math.floor(d.getMonth() / 3);
  const year = d.getFullYear();
  const startMonth = quarter * 3;
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0);
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd: end.toISOString().slice(0, 10),
  };
}

/** Get monthly period boundaries */
export function getMonthBoundaries(date: string): { periodStart: string; periodEnd: string } {
  const d = new Date(date);
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd: end.toISOString().slice(0, 10),
  };
}

// ── Plan gating ────────────────────────────────────────────────────────────

/**
 * Accounting is a Scale-and-up feature — expressed as a rung, not a list.
 *
 * The list read `['scale', 'command', 'enterprise']` and so excluded HOTEL, which sits on
 * Scale's own rung: a hotel paying for a tier above Operate had no accounting at all.
 * A `>=` on the shared ladder cannot forget a tier the way a literal can.
 */
export const ACCOUNTING_MIN_PLAN: PlanTier = 'scale';

export function isAccountingEligible(plan: string): boolean {
  return meetsMinPlan(plan as PlanTier, ACCOUNTING_MIN_PLAN);
}
